"use client";

import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import { ChevronDown, Menu, PlayCircle, X } from "lucide-react";

const Spline = lazy(() => import("@splinetool/react-spline"));

function HeroSplineBackground() {
  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100vh",
        pointerEvents: "auto",
        overflow: "hidden",
      }}
    >
      <Suspense fallback={<div className="h-screen w-full bg-black" />}>
        <Spline
          style={{
            width: "100%",
            height: "100vh",
            pointerEvents: "auto",
          }}
          scene="https://prod.spline.design/us3ALejTXl6usHZ7/scene.splinecode"
        />
      </Suspense>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100vh",
          background:
            "linear-gradient(to right, rgba(0, 0, 0, 0.8), transparent 30%, transparent 70%, rgba(0, 0, 0, 0.8)), linear-gradient(to bottom, transparent 50%, rgba(0, 0, 0, 0.9))",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

function ScreenshotSection({ screenshotRef }) {
  return (
    <section className="relative z-10 container mx-auto px-4 md:px-6 lg:px-8 mt-11 md:mt-12">
      <div
        ref={screenshotRef}
        className="bg-gray-900 rounded-xl overflow-hidden shadow-2xl border border-gray-700/50 w-full md:w-[80%] lg:w-[70%] mx-auto"
      >
        <img
          src="https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1920&q=80"
          alt="Creative workflow dashboard preview"
          className="w-full h-auto block rounded-lg mx-auto"
        />
      </div>
    </section>
  );
}

function HeroContent() {
  return (
    <div className="text-left text-white pt-16 sm:pt-24 md:pt-32 px-4 max-w-3xl">
      <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold mb-4 leading-tight tracking-wide">
        Elevate your <br className="sm:hidden" />
        creative workflow
        <br className="sm:hidden" /> to an art form.
      </h1>
      <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 opacity-80 max-w-xl">
        Manage all of your media and assets - video, photos, design files,
        docs, PDFs, and more - on a single secure surface to create and deliver
        high-quality content faster.
      </p>
      <div className="flex pointer-events-auto flex-col sm:flex-row items-start space-y-3 sm:space-y-0 sm:space-x-3">
        <button
          className="bg-[#8200DB29] hover:bg-black/50 text-white font-semibold py-2 sm:py-3 px-6 sm:px-8 rounded-full transition duration-300 w-full sm:w-auto border border-[#322D36]"
          style={{ backdropFilter: "blur(8px)" }}
        >
          Start Free Trial
        </button>
        <button className="pointer-events-auto bg-[#0009] border border-gray-600 hover:border-gray-400 text-gray-200 hover:text-white font-medium py-2 sm:py-3 px-6 sm:px-8 rounded-full transition duration-300 flex items-center justify-center w-full sm:w-auto">
          <PlayCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
          Watch the Video
        </button>
      </div>
    </div>
  );
}

function Navbar() {
  const [hoveredNavItem, setHoveredNavItem] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileDropdowns, setMobileDropdowns] = useState({
    features: false,
    enterprise: false,
    resources: false,
  });

  const handleMouseEnterNavItem = (item) => setHoveredNavItem(item);
  const handleMouseLeaveNavItem = () => setHoveredNavItem(null);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (isMobileMenuOpen) {
      setMobileDropdowns({
        features: false,
        enterprise: false,
        resources: false,
      });
    }
  };

  const toggleMobileDropdown = (key) => {
    setMobileDropdowns((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const navLinkClass = (itemName, extraClasses = "") => {
    const isCurrentItemHovered = hoveredNavItem === itemName;
    const isAnotherItemHovered = hoveredNavItem !== null && !isCurrentItemHovered;

    const colorClass = isCurrentItemHovered
      ? "text-white"
      : isAnotherItemHovered
        ? "text-gray-500"
        : "text-gray-300";

    return `text-sm transition duration-150 ${colorClass} ${extraClasses}`;
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
        setMobileDropdowns({
          features: false,
          enterprise: false,
          resources: false,
        });
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobileMenuOpen]);

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-20"
      style={{
        backgroundColor: "rgba(13, 13, 24, 0.3)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        borderRadius: "0 0 15px 15px",
      }}
    >
      <div className="container mx-auto px-4 py-4 md:px-6 lg:px-8 flex items-center justify-between">
        <div className="flex items-center space-x-6 lg:space-x-8">
          <div className="text-white text-xl font-bold">FC</div>

          <div className="hidden lg:flex items-center space-x-6">
            <div
              className="relative group"
              onMouseEnter={() => handleMouseEnterNavItem("features")}
              onMouseLeave={handleMouseLeaveNavItem}
            >
              <a href="#features" className={navLinkClass("features", "flex items-center")}>
                Features
                <ChevronDown className="ml-1 w-3 h-3 group-hover:rotate-180 transition-transform duration-200" />
              </a>
            </div>

            <div
              className="relative group"
              onMouseEnter={() => handleMouseEnterNavItem("enterprise")}
              onMouseLeave={handleMouseLeaveNavItem}
            >
              <a href="#enterprise" className={navLinkClass("enterprise", "flex items-center")}>
                Enterprise
                <ChevronDown className="ml-1 w-3 h-3 group-hover:rotate-180 transition-transform duration-200" />
              </a>
            </div>

            <div
              className="relative group"
              onMouseEnter={() => handleMouseEnterNavItem("resources")}
              onMouseLeave={handleMouseLeaveNavItem}
            >
              <a href="#resources" className={navLinkClass("resources", "flex items-center")}>
                Resources
                <ChevronDown className="ml-1 w-3 h-3 group-hover:rotate-180 transition-transform duration-200" />
              </a>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-4 md:space-x-6">
          <a href="#contact" className="hidden md:block text-gray-300 hover:text-white text-sm">
            Contact Sales
          </a>
          <a href="#signin" className="hidden sm:block text-gray-300 hover:text-white text-sm">
            Sign In
          </a>
          <a
            href="#start"
            className="bg-[#8200DB29] hover:bg-black/50 text-white font-semibold py-2 px-5 rounded-full text-sm md:text-base border border-[#322D36]"
            style={{ backdropFilter: "blur(8px)" }}
          >
            Start Free Trial
          </a>
          <button
            className="lg:hidden text-white p-2"
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <div
        className={`lg:hidden bg-black bg-opacity-50 border-t border-gray-700/30 absolute top-full left-0 right-0 z-30
        overflow-hidden transition-all duration-300 ease-in-out
        ${isMobileMenuOpen ? "max-h-screen opacity-100 pointer-events-auto" : "max-h-0 opacity-0 pointer-events-none"}`}
        style={{ backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
      >
        <div className="px-4 py-6 flex flex-col space-y-4">
          {["features", "enterprise", "resources"].map((item) => (
            <div className="relative" key={item}>
              <button
                className="text-gray-300 hover:text-gray-100 flex items-center justify-between w-full text-left text-sm py-2 capitalize"
                onClick={() => toggleMobileDropdown(item)}
                aria-expanded={mobileDropdowns[item]}
              >
                {item}
                <ChevronDown
                  className={`ml-2 w-3 h-3 transition-transform duration-200 ${mobileDropdowns[item] ? "rotate-180" : ""}`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>
    </nav>
  );
}

export function HeroSection() {
  const screenshotRef = useRef(null);
  const heroContentRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (screenshotRef.current && heroContentRef.current) {
        requestAnimationFrame(() => {
          const scrollPosition = window.pageYOffset;
          screenshotRef.current.style.transform = `translateY(-${scrollPosition * 0.5}px)`;

          const maxScroll = 400;
          const opacity = 1 - Math.min(scrollPosition / maxScroll, 1);
          heroContentRef.current.style.opacity = opacity.toString();
        });
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="relative">
      <Navbar />

      <div className="relative min-h-screen">
        <div className="absolute inset-0 z-0 pointer-events-auto">
          <HeroSplineBackground />
        </div>

        <div
          ref={heroContentRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100vh",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            zIndex: 10,
            pointerEvents: "none",
          }}
        >
          <div className="container mx-auto">
            <HeroContent />
          </div>
        </div>
      </div>

      <div className="bg-black relative z-10" style={{ marginTop: "-10vh" }}>
        <ScreenshotSection screenshotRef={screenshotRef} />
        <div className="container mx-auto px-4 py-16 text-white">
          <h2 className="text-4xl font-bold text-center mb-8">Other Content Below</h2>
          <p className="text-center max-w-xl mx-auto opacity-80">
            This is where additional sections of your landing page would go.
          </p>
        </div>
      </div>
    </div>
  );
}
